/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

// Import necessary types and classes from Angular forms
import {
	type AbstractControl,
	type AsyncValidatorFn,
	FormArray,
	FormControl,
	FormGroup,
	type ValidatorFn,
	Validators,
} from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { environment } from "src/environments/environment";
import { INVALID_EMAIL_DOMAINS } from "./invalid-email-domains.const";

// Define a custom validator class that takes a validator function as an argument
export type CbFormValidator = (
	formGroup: CbFormGroup,
	controlName: string,
) => Record<
	string,
	boolean | string | { message: string; attr: Record<string, unknown> }
> | null;

export type CbFormAsyncValidator = (
	formGroup: CbFormGroup,
	controlName: string,
) => Promise<Record<
	string,
	boolean | string | { message: string; attr: Record<string, unknown> }
> | null>;

export type CbFormGlobalValidator = (
	formGroup: CbFormGroup,
) => Record<
	string,
	boolean | string | { message: string; attr: Record<string, unknown> }
> | null;

export type CbFormGlobalAsyncValidator = (
	formGroup: CbFormGroup,
) => Promise<Record<
	string,
	boolean | string | { message: string; attr: Record<string, unknown> }
> | null>;

// biome-ignore lint/complexity/noStaticOnlyClass: <explanation>
export class CbValidator {
	public static email: CbFormValidator = (
		formGroup: CbFormGroup,
		controlName: string,
	) => {
		const control = formGroup.get(controlName);
		const value = control?.value;

		// Regex for email validation
		const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
		if (value && value.length > 0 && !emailRegex.test(value))
			return { email: "COMMON.FORM_ERRORS.INVALID_EMAIL" };

		return null;
	};

	public static allowedEmailDomain: CbFormValidator = (
		formGroup: CbFormGroup,
		controlName: string,
	) => {
		const control = formGroup.get(controlName);

		// Check if the email domain is valid (not in the list of banned ones)
		const invalid_emails = INVALID_EMAIL_DOMAINS;
		const email = control?.value;
		const domain = email?.split("@")[1];
		if (environment.production && invalid_emails.includes(domain))
			return { email: "COMMON.FORM_ERRORS.PROHIBITED_DOMAIN" };

		return null;
	};

	public static strongPassword: CbFormValidator = (
		formGroup: CbFormGroup,
		controlName: string,
	) => {
		const control = formGroup.get(controlName);

		// Regex for password validation
		// The password must contain at least 8 characters, including at least 1 uppercase letter, 1 lowercase letter, and 1 number
		const passwordRegex = /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{8,}/;
		return passwordRegex.test(control?.value)
			? null
			: { password: "COMMON.FORM_ERRORS.NOT_STRONG_PASSWORD" };
	};

	public static min: (value: number) => CbFormValidator = (value: number) => {
		return (formGroup: CbFormGroup, controlName: string) => {
			const control = formGroup.get(controlName);
			return control?.value >= value
				? null
				: {
						min: {
							message: "COMMON.FORM_ERRORS.MIN_VALUE",
							attr: { min: value },
						},
					};
		};
	};

	public static max: (value: number) => CbFormValidator = (value: number) => {
		return (formGroup: CbFormGroup, controlName: string) => {
			const control = formGroup.get(controlName);
			return control?.value <= value
				? null
				: {
						max: {
							message: "COMMON.FORM_ERRORS.MAX_VALUE",
							attr: { max: value },
						},
					};
		};
	};

	public static minLength: (value: number) => CbFormValidator = (
		value: number,
	) => {
		return (formGroup: CbFormGroup, controlName: string) => {
			const control = formGroup.get(controlName);
			return control?.value.length >= value
				? null
				: {
						minLength: {
							message: "COMMON.FORM_ERRORS.MIN_LENGTH",
							attr: { min: value },
						},
					};
		};
	};

	public static maxLength: (value: number) => CbFormValidator = (
		value: number,
	) => {
		return (formGroup: CbFormGroup, controlName: string) => {
			const control = formGroup.get(controlName);
			return control?.value?.toString().length <= value
				? null
				: {
						maxLength: {
							message: "COMMON.FORM_ERRORS.MAX_LENGTH",
							attr: { max: value },
						},
					};
		};
	};

	public static pattern: (value: string) => CbFormValidator = (
		value: string,
	) => {
		return (formGroup: CbFormGroup, controlName: string) => {
			const control = formGroup.get(controlName);
			return new RegExp(value).test(control?.value) ? null : { pattern: true };
		};
	};
}

type CbFormGroupSingleFieldType = {
	initialValue: unknown;
	required?: boolean;
	disabled?: boolean | ((formGroup: CbFormGroup) => boolean);
	onChange?: (formGroup: CbFormGroup) => void;
	validators?: (CbFormValidator | CbFormAsyncValidator)[]; // Validators can be Angular's ValidatorFn or custom validators
};

type CbFormGroupArrayFieldType = {
	initialValue: Record<string, unknown>[];
	minRows?: number;
	maxRows?: number;
	fields: Record<
		string,
		CbFormGroupSingleFieldType /* & {
			onChange?: (formGroup: CbFormGroup, rowIndex: number) => void;
		} */
	>;
	formatResult?: (result: Record<string, unknown>) => unknown;
	parseValue?: (value: unknown) => Record<string, unknown>;
};

// Define a type for form group fields, which includes initial value and validators
export type CbFormGroupFieldType =
	| CbFormGroupSingleFieldType
	| CbFormGroupArrayFieldType;

// Define a class for form groups
export class CbFormGroup extends FormGroup {
	private readonly destroy$ = new Subject<void>();

	constructor(
		public form: Record<string, CbFormGroupFieldType>,
		public globalValidators: (
			| CbFormGlobalValidator
			| CbFormGlobalAsyncValidator
		)[] = [],
	) {
		super({});
		this.initAllControls();
	}

	public get valuesObject(): Record<string, unknown> {
		const values: Record<string, unknown> = {};
		for (const [key, value] of Object.entries(this.form)) {
			const control = this.get(key);
			const config = this.form[key];

			if (control) {
				// If a format function is defined for the form array, use it to format the result
				if ("formatResult" in config && config.formatResult) {
					const result = [];
					for (const v of control.value) {
						result.push(config.formatResult(v));
					}
					values[key] = result;
				}
				// Else just set the raw value es. "this-is-a-text" or { key1: value1, key2: value2 } etc.
				else {
					values[key] = this.get(key)?.value;
				}
			}
		}
		return values;
	}

	public getArrayControl(name: string): FormArray | undefined {
		return this.get(name) as FormArray | undefined;
	}

	/**
	 * Check if a control exists in the form group
	 * @param name - The name of the control
	 * @returns True if the control exists, false otherwise
	 */
	public hasControl(name: string): boolean {
		return this.get(name) !== null;
	}

	/**
	 * Patch a control value in the form group
	 * @param name - The name of the control
	 * @param value - The value to patch
	 */
	public patchControlValue(name: string, value: unknown) {
		const control = this.get(name);
		if (control) {
			const config = this.form[name];

			if ("fields" in config && Array.isArray(value)) {
				for (const v of value) {
					let patchValue = v;
					if (config.parseValue) {
						patchValue = config.parseValue(v);
					}

					this.addArrayRow(name, patchValue);
				}
			} else {
				control.patchValue(value);
			}
		}
	}

	public initAllControls() {
		const controls: Record<string, AbstractControl> = this.createControls(
			this.form,
		);

		for (const [key, value] of Object.entries(controls)) {
			this.addControl(key, value);
		}

		// Subscribe to value changes to check for disabled status
		this.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.checkDisabledStatus(this.form);
		});

		// Add global validators
		const globalValidators = [];
		for (const validator of this.globalValidators) {
			globalValidators.push(this.createGlobalValidator(validator));
		}
		this.setValidators(globalValidators);
	}

	// This function creates form controls for each field in the form group
	private createControls(
		formDefinition: Record<string, CbFormGroupFieldType>,
	): Record<string, AbstractControl> {
		// Initialize an empty object to hold the form controls
		const formControls: Record<string, AbstractControl> = {};

		// Iterate over each field in the form group
		for (const [fieldName, fieldDefinition] of Object.entries(formDefinition)) {
			// If the field has a "fields" property, then it's a form array
			if ("fields" in fieldDefinition) {
				const formArrayDefinition: CbFormGroupArrayFieldType =
					fieldDefinition as CbFormGroupArrayFieldType;

				// Check that the maximum number of rows is not less than the minimum number of rows
				if (
					formArrayDefinition.maxRows !== undefined &&
					formArrayDefinition.minRows !== undefined &&
					formArrayDefinition.maxRows < formArrayDefinition.minRows
				) {
					throw new Error("Max rows cannot be less than min rows");
				}

				// Create form controls for each field in the form array
				const formArrayControls: AbstractControl[] = [];

				// Iterate over each initial value in the form array
				for (const initialRowValue of formArrayDefinition.initialValue) {
					const childControls = this.createControls(formArrayDefinition.fields);
					const formGroup = new FormGroup({});

					let objectToPatch = initialRowValue;
					if (formArrayDefinition.parseValue) {
						objectToPatch = formArrayDefinition.parseValue(initialRowValue);
					}

					for (const [rowKey, rowControl] of Object.entries(childControls)) {
						rowControl.patchValue(objectToPatch[rowKey]);
						formGroup.addControl(rowKey, rowControl);
					}
					formArrayControls.push(formGroup);
				}

				// Add empty elements from 0 to (minRows - formArrayControls.length)
				const requiredMinRows =
					(formArrayDefinition.minRows ?? 0) - formArrayControls.length;
				if (requiredMinRows > 0) {
					for (let i = 0; i < requiredMinRows; i++) {
						const childControls = this.createControls(
							formArrayDefinition.fields,
						);
						const formGroup = new FormGroup({});
						for (const [rowKey, rowControl] of Object.entries(childControls)) {
							rowControl.patchValue(
								formArrayDefinition.fields[rowKey].initialValue ?? undefined,
							);
							formGroup.addControl(rowKey, rowControl);
						}
						formArrayControls.push(formGroup);
					}
				}

				// Add the form array to the form controls
				formControls[fieldName] = new FormArray(formArrayControls);
			} else {
				// If the field does not have a "fields" property, then it's a single form field
				const formControlDefinition: CbFormGroupSingleFieldType =
					fieldDefinition as CbFormGroupSingleFieldType;

				// Check if the field is disabled
				const isDisabled =
					typeof formControlDefinition.disabled === "function"
						? formControlDefinition.disabled(this)
						: formControlDefinition.disabled;

				const controlValidators = formControlDefinition.validators
					? this.createValidators(formControlDefinition.validators, fieldName)
					: [];

				// Add required validator if the field is required
				if (formControlDefinition.required) {
					controlValidators.push(Validators.required);
				}

				// Create a new form control with the initial value and validators
				const formControl = new FormControl(
					{
						value: formControlDefinition.initialValue,
						disabled: isDisabled ?? false,
					},
					controlValidators,
				);

				if (formControlDefinition.onChange) {
					formControl.valueChanges
						.pipe(takeUntil(this.destroy$))
						.subscribe(() => {
							if (formControlDefinition.onChange) {
								formControlDefinition.onChange(this);
							}
						});
				}

				// Add the form control to the form controls
				formControls[fieldName] = formControl;
			}
		}

		// Return the form controls
		return formControls;
	}

	/**
	 * Check the disabled status of each field in the form group
	 */
	private checkDisabledStatus(form: Record<string, CbFormGroupFieldType>) {
		for (const [key, value] of Object.entries(form)) {
			const control = this.get(key);
			if (!control) continue;

			if ("fields" in value) {
				const formArray: CbFormGroupArrayFieldType =
					value as CbFormGroupArrayFieldType;

				if (formArray.fields) {
					this.checkDisabledStatus(formArray.fields);
				}
			} else {
				const formControl: CbFormGroupSingleFieldType =
					value as CbFormGroupSingleFieldType;

				if (formControl.disabled) {
					if (typeof formControl.disabled === "function") {
						const result = formControl.disabled(this);

						if (result) {
							control.disable({ emitEvent: false });
						} else {
							control.enable({ emitEvent: false });
						}
					} else {
						if (formControl.disabled === true) {
							control.disable({ emitEvent: false });
						} else {
							control.enable({ emitEvent: false });
						}
					}
				}
			}
		}
	}

	// Create validators for each field in the form group
	private createValidators(
		validators: (CbFormValidator | CbFormAsyncValidator)[],
		formControlName: string,
	): (ValidatorFn | AsyncValidatorFn)[] {
		return validators.map((validator) => {
			return (control: AbstractControl) => {
				return validator(this, formControlName);
			};
		});
	}

	// Create global validators for the form group
	private createGlobalValidator(
		validator: CbFormGlobalValidator | CbFormGlobalAsyncValidator,
	): ValidatorFn | AsyncValidatorFn {
		return (control: AbstractControl) => {
			return validator(this);
		};
	}

	public addArrayRows(name: string, values: unknown[]): void {
		for (const v of values) {
			this.addArrayRow(name, v);
		}
	}

	public addArrayRow(name: string, value?: unknown): void {
		const formArray = this.get(name) as FormArray;

		if ("fields" in this.form[name]) {
			const childControls = (this.form[name] as CbFormGroupArrayFieldType)
				.fields;
			const fg = new FormGroup(this.createControls(childControls));

			if (value) {
				fg.patchValue(value);
			}

			formArray.push(fg);
		}
	}

	public removeArrayRow(name: string, index: number): void {
		const formArray = this.get(name) as FormArray;
		console.log(formArray, name, index);
		formArray.removeAt(index);
	}

	public getArrayMaxRows(name: string): number {
		return (this.form[name] as CbFormGroupArrayFieldType).maxRows ?? 9999;
	}

	public getArrayMinRows(name: string): number {
		return (this.form[name] as CbFormGroupArrayFieldType).minRows ?? 0;
	}

	public destroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
